import { Button, Edit, OTPInput } from "@monsterindia/global-ui-components";
import React, { useEffect, useState } from "react";

import {
  API_ENDPOINT,
  getRecaptchaSiteKey,
} from "../../../../constants/appConstant";
import { useNetwork } from "../../../../hooks/useNetwork";
import useRecaptchaV3 from "../../../../hooks/useRecaptchaV3";
import { MobileOTPProps } from "../../../../types";
import { encryptPayload } from "../../../utils/encryptionUtils";
import { ApplyForJobCard } from "../../common/ApplyForJobCard";
import { Heading } from "../../common/Heading";
import { Events } from "../../common/json/clevertapEvents";

type NetworkType = {
  actionType: string;
  [key: string]: any;
};

export const MobileOTP = ({
  updateStepHandler,
  countryCode,
  mobileNumber,
  toast,
  otpId,
  nextApopType,
  onCTA,
  closeApop,
  isRecaptchaEnabled = true,
  flowType,
  jobData,
  verifyType,
  handleEventTracking,
}: MobileOTPProps) => {
  const [showResend, setShowResend] = useState(false);
  const [otpValue, setOTPValue] = useState("");
  const [mobileOTPId, setMobileOTPId] = useState(otpId);
  const { response: fetchResponse, fetchData: fetchAPI } = useNetwork();
  const { response: apiResponse, fetchData: submitFetchApi } = useNetwork();
  const executeRecaptcha = useRecaptchaV3(
    getRecaptchaSiteKey(window.location.host),
    isRecaptchaEnabled,
  );

  const otpUpdateHandler = (newOTP: string) => {
    setOTPValue(newOTP);
  };

  const verifyOtpHandler = async () => {
    const res = await submitFetchApi(
      `${API_ENDPOINT().seekerProfile.verifyOTPMiddleware}`,
      "POST",
      {},
      {
        mobileNumber: {
          countryCode: countryCode,
          mobileNumber: mobileNumber,
        },
        otp: otpValue,
        otpId: mobileOTPId || otpId,
      },
    );

    if ((res?.data as any)?.successMessage) {
      if (flowType === "PROFILE_NUDGE") {
        // handle profile nudge flow
        if (verifyType === "ADD") {
          handleEventTracking({
            eventType: "eventPush",
            eventName: Events["VERIFICATION"]?.addAndVerified?.["EVENTNAME"],
            eventProps: Events["VERIFICATION"]?.addAndVerified?.["EVENTOBJ"],
          });
        } else if (verifyType === "UPDATE") {
          handleEventTracking({
            eventType: "eventPush",
            eventName: Events["VERIFICATION"]?.updateAndVerify?.["EVENTNAME"],
            eventProps: Events["VERIFICATION"]?.updateAndVerify?.["EVENTOBJ"],
          });
        } else {
          handleEventTracking({
            eventType: "eventPush",
            eventName: Events["VERIFICATION"]?.verified?.["EVENTNAME"],
            eventProps: Events["VERIFICATION"]?.verified?.["EVENTOBJ"],
          });
        }
        const nextNudgeRes = await fetchAPI(
          `${API_ENDPOINT().seekerProfile.nextNudge}`,
          "GET",
        );

        if (
          nextNudgeRes.data &&
          (nextNudgeRes.data as any).nextActionRequired
        ) {
          const { actionType: apiActionType, ...rest } = (
            nextNudgeRes.data as any
          ).nextActionRequired as NetworkType;

          onCTA && onCTA("MOBILE_OTP");
          toast({
            showToast: true,
            text:
              (res.data as any).successMessage ??
              "Section updated successfully",
            type: "success",
          });
          nextApopType(apiActionType ?? "", rest);
        } else {
          toast({
            showToast: true,
            text: "Something went wrong! Please try again.",
            type: "error",
          });
        }
      } else {
        // validate api call
        const validateRes = await fetchAPI(
          `${API_ENDPOINT().seekerProfile.validateProfileMiddleware}`,
          "GET",
        );

        if (validateRes.data) {
          const { actionType: apiActionType, ...rest } =
            validateRes.data as NetworkType;

          onCTA && onCTA("MOBILE_OTP");
          toast({
            showToast: true,
            text:
              (res.data as any).successMessage ??
              "Section updated successfully",
            type: "success",
          });
          nextApopType(apiActionType, rest);
        } else {
          toast({
            showToast: true,
            text: "Something went wrong!",
            type: "error",
          });
        }
      }
    } else if (res.error) {
      toast({
        showToast: true,
        text:
          (res.error as any)?.error?.message?.errorMessage ??
          "Something went wrong!",
        type: "error",
      });
    }
  };

  const getOTP = async () => {
    let { reCaptchaToken, reCaptchaAction } =
      await executeRecaptcha("resendotp");

    const apiData = isRecaptchaEnabled
      ? {
          username: `+${countryCode}${mobileNumber}`,
          reCaptchaToken,
          reCaptchaAction,
        }
      : { username: `+${countryCode}${mobileNumber}` };
    const encryptedPayload = encryptPayload(apiData);
    const res = await fetchAPI(
      API_ENDPOINT().seekerProfile.sendOTPMiddleware,
      "POST",
      {},
      { payload: encryptedPayload },
    );

    const id = (res?.data as any)?.id;

    if (id) {
      setMobileOTPId(id);
      updateStepHandler("MOBILE_OTP");
      toast({
        showToast: true,
        text: "OTP sent successfully",
        type: "success",
      });
    } else {
      toast({
        showToast: true,
        text: "Something went wrong!",
        type: "error",
      });
    }
  };

  useEffect(() => {
    if (!showResend) {
      setTimeout(() => {
        setShowResend(true);
      }, 20000);
    }
  }, [showResend]);

  const handleResendClick = () => {
    getOTP();
    setOTPValue("");
    setShowResend(false);
  };

  return (
    <>
      <Heading
        headingText={
          flowType === "PROFILE_NUDGE" && jobData
            ? "Job Application"
            : "Verify Number"
        }
        onCloseBtnClickHandler={closeApop}
        isMobile={window.innerWidth < 786}
      />
      <div
        className="l1-mb-16 l1-mt-14 l1-flex l1-w-full l1-flex-col l1-gap-6 md:l1-mb-12 md:l1-mt-12"
        id="mobileOTPForm"
      >
        {jobData ? (
          <ApplyForJobCard
            jobId={jobData.jobId}
            jobTitle={jobData.jobTitle}
            companyName={jobData.companyName}
            companyLogoUrl={jobData.companyLogoUrl}
          />
        ) : null}
        <div className="l1-flex l1-flex-col l1-gap-4">
          {flowType === "PROFILE_NUDGE" ? (
            <h4 className="l1-text-2xl l1-font-bold l1-text-content-primary">
              Verify Number
            </h4>
          ) : null}
          <div className="l1-flex l1-flex-col l1-items-start l1-gap-0 l1-text-sm">
            {flowType === "PROFILE_NUDGE" ? (
              <p className="l1-text-content-secondary">
                Verify your number for recruiters to contact you.
              </p>
            ) : (
              <p className="l1-text-content-secondary">
                We have sent an OTP to your phone number
              </p>
            )}
            <Button
              type="button"
              className="!l1-w-max !l1-px-0 !l1-font-normal !l1-text-state-content-link-bold"
              modifier="tertiary"
              rightIcon={<Edit width={16} height={16} />}
              label={`+${countryCode}${mobileNumber}`}
              onClick={() => updateStepHandler("CHANGE_NUMBER")}
            />
          </div>

          <OTPInput
            otpValue={otpValue}
            otpUpdateHandler={otpUpdateHandler}
            resendClickHandler={handleResendClick}
            resendTime={60}
          />
        </div>

        <div className="l1-fixed l1-bottom-0 l1-left-0 l1-z-10 l1-flex l1-w-full l1-max-w-full l1-gap-2 l1-bg-white l1-px-4 l1-py-3 l1-shadow-black-sm md:!l1-px-8 md:l1-py-4">
          <div
            className={
              flowType === "PROFILE_NUDGE" ? "l1-ml-auto l1-w-max" : "l1-w-full"
            }
          >
            <Button
              className={
                flowType === "PROFILE_NUDGE"
                  ? "!l1-left-0 !l1-w-[200px] !l1-py-3"
                  : "!l1-w-full !l1-py-3"
              }
              label="Verify Mobile Number"
              modifier="primary"
              type="button"
              onClick={() => verifyOtpHandler()}
              isLoading={apiResponse.isLoading || fetchResponse.isLoading}
              disabled={otpValue.length === 6 ? false : true}
            />
          </div>
        </div>
      </div>
    </>
  );
};
